import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import Page from '../../components/layouts/page'
import { Message, Modal, Table, Tag } from '@bitcine/cinesend-theme/dist'
import useTableFilters from '../../hooks/use_table_filters'
import { humanFileSize } from '../../helpers/human_file_size'
import { useState } from 'react'
import ReactJson from 'react-json-view'
import { convertToShorthandWithTimezone } from '../../helpers/convert_date'

const SyncStatusEntry = ({ label, data }) => {
  const lastRun = data?.last_run
  const lastSuccess = data?.last_successful_run

  if (!lastRun) {
    return (
      <span>
        <strong>{label}:</strong> No sync has run yet.
      </span>
    )
  }

  if (lastRun.status === 'pending') {
    return (
      <span>
        <strong>{label}:</strong> Sync in progress&hellip;
      </span>
    )
  }

  if (lastRun.status === 'success') {
    return (
      <span>
        <strong>{label}:</strong> Last synced {convertToShorthandWithTimezone(lastRun.created_at)} &mdash;{' '}
        {lastRun.files_synced} file(s)
      </span>
    )
  }

  if (lastRun.status === 'error') {
    return (
      <span>
        <strong>{label}:</strong> Last sync failed ({convertToShorthandWithTimezone(lastRun.created_at)}).{' '}
        {lastSuccess ? (
          <>
            Showing data from last successful sync: {convertToShorthandWithTimezone(lastSuccess.created_at)} &mdash;{' '}
            {lastSuccess.files_synced} file(s).
          </>
        ) : (
          'No successful sync on record.'
        )}
      </span>
    )
  }

  return null
}

const SyncStatusBanner = ({ mirrorRuns }) => {
  if (!mirrorRuns) return null

  const hasError = mirrorRuns.audio?.last_run?.status === 'error' || mirrorRuns.video?.last_run?.status === 'error'

  return (
    <Message warning={hasError} info={!hasError} small className='mb-4'>
      <div className='flex flex-col gap-1 text-xs'>
        <SyncStatusEntry label='Audio' data={mirrorRuns.audio} />
        <SyncStatusEntry label='Video' data={mirrorRuns.video} />
      </div>
    </Message>
  )
}

const Files = () => {
  const [modalOpen, setModalOpen] = useState(null)
  const { airlineID } = useParams()

  const sortingOptions = [
    { label: 'File Name', key: 'uploaded_file_name' },
    // { label: 'File Size', key: 'file_size' },
    { label: 'Status', key: 'status' },
    { label: 'Matched Status', key: 'is_matched' },
  ]

  // const filteringOptions = [
  //   {
  //     label: 'Matched Status',
  //     key: 'is_matched',
  //     type: 'checkboxes',
  //     options: [
  //       { label: 'Matched', key: 'true' },
  //       { label: 'Unmatched', key: 'false' },
  //     ],
  //   },
  // ]

  const { queryString, searching, sorting, filtering, pagination } = useTableFilters({
    sortingOptions,
    // filteringOptions,
    enableSearch: true,
  })

  const {
    data: externalFiles,
    isLoading,
    error,
  } = useQuery({
    queryKey: [`airlines/${airlineID}/external-files-mirror`, queryString],
    enabled: !!airlineID,
  })

  return (
    <Page title='External Files'>
      <SyncStatusBanner mirrorRuns={externalFiles?.mirror_runs} />
      <Table
        status={{
          pending: isLoading,
          error: error,
          errorMessage: error?.message,
        }}
        widths={['auto', 100, 100, 300, 300, 100, 100, 100]}
        header={{
          columns: [
            { text: 'File Name', key: 'uploaded_file_name' },
            { text: 'File Size' },
            { text: 'Supplier', key: 'supplier' },
            { text: 'Audio Languages' },
            { text: 'Text Track Languages' },
            { text: 'Received' },
            { text: 'Status', key: 'status' },
            { text: 'Matched', key: 'is_matched' },
          ],
          searching,
          sorting,
          // filtering,
        }}
        body={{
          data: externalFiles?.files?.data || [],
          row: {
            compact: true,
            spaced: true,
            onClick: (_, data) => {
              setModalOpen(data?.raw_response ?? {})
            },
            render: [
              (data) => <div className='text-xs overflow-hidden'>{data.file_name}</div>,
              (data) => <div className='text-xs'>{humanFileSize(data.file_size)}</div>,
              (data) => <div className='text-xs'>{data.supplier ?? 'N/A'}</div>,
              (data) => {
                const unknownLanguages = data.unknown_languages || []
                return (
                  <div className='text-xs'>
                    {data.audio_languages?.length > 0 ? (
                      <div className='flex flex-wrap gap-1'>
                        {data.audio_languages.map((lang, index) => (
                          <Tag
                            key={index}
                            outline
                            size='small'
                            type={unknownLanguages.includes(lang) ? 'error' : 'neutral'}
                            label={lang}
                          />
                        ))}
                      </div>
                    ) : (
                      <span>None</span>
                    )}
                  </div>
                )
              },
              (data) => {
                const unknownLanguages = data.unknown_languages || []
                return (
                  <div className='text-xs'>
                    {data.text_track_languages?.length > 0 ? (
                      <div className='flex flex-wrap gap-1'>
                        {data.text_track_languages.map((lang, index) => (
                          <Tag
                            key={index}
                            outline
                            size='small'
                            type={unknownLanguages.includes(lang) ? 'error' : 'neutral'}
                            label={lang}
                          />
                        ))}
                      </div>
                    ) : (
                      <span>None</span>
                    )}
                  </div>
                )
              },
              (data) => <div className='text-xs'>{convertToShorthandWithTimezone(data.received_time)}</div>,
              (data) => (
                <Tag
                  outline
                  type={
                    data.status === 'completed'
                      ? 'success'
                      : data.status === 'completed-with-warnings'
                        ? 'warning'
                        : 'error'
                  }
                  label={data.status?.toUpperCase()}
                />
              ),
              (data) => (
                <Tag
                  outline
                  type={data.is_matched ? 'success' : 'warning'}
                  label={data.is_matched ? 'Matched' : 'Unmatched'}
                />
              ),
            ],
          },
        }}
        empty={{
          title: 'No external files found!',
          text: 'No files have been synced yet.',
          icon: 'folder',
        }}
        paginate={{
          ...pagination,
          totalRows: externalFiles?.files?.total || 0,
        }}
      />
      {modalOpen && (
        <Modal header={'Raw Response'} onClose={() => setModalOpen(null)}>
          <ReactJson src={modalOpen} />
        </Modal>
      )}
    </Page>
  )
}

export default Files
